import {
  Combobox,
  ComboboxTarget,
  useCombobox,
  InputBase,
  Text,
  Box,
  Group,
  useMantineTheme,
  Tooltip,
  rem,
} from "@mantine/core";
import BotMessageSquareIcon from "../../../shared/svg/BotMessage";
import { useMediaQuery } from "@mantine/hooks";
import { AITooltip } from "../../../utils/components/AITooltip";
import { useTranslation } from "react-i18next";

export function CalendarSelectWithIcons({
  data,
  value,
  onChange,
}: {
  data: { label: string; value: string; autogenerated: boolean | undefined }[];
  value: string | null;
  onChange: (val: string | null) => void;
}) {
  const combobox = useCombobox();
  const theme = useMantineTheme();
  const mobile = useMediaQuery("(max-width: 650px)");
  const { t } = useTranslation();
  const commonStyles: React.CSSProperties = {
    backgroundColor: theme.colors.gray[1],
    color: theme.colors.dark[7],
    fontSize: rem(14),
    padding: `${rem(4)} ${rem(8)}`,
    borderRadius: rem(4),
    boxShadow: theme.shadows.sm,
    maxWidth: mobile ? rem(300) : rem(400),
    wordWrap: "break-word" as "break-word",
    whiteSpace: "normal",
  };

  // Для тултіпа — пристрій з touch чи ні
  const isTouchDevice =
    typeof window !== "undefined" && "ontouchstart" in window;

  const selectedItem = data.find((d) => d.value === value);

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        onChange(val);
        combobox.closeDropdown();
      }}
    >
      <ComboboxTarget>
        <InputBase
          component="button"
          type="button"
          pointer
          rightSection={
            <Combobox.Chevron onClick={() => combobox.toggleDropdown()} />
          }
          onClick={() => combobox.toggleDropdown()}
          w={mobile ? 240 : 300}
        >
          <Group
            id="select-calendar"
            justify="space-between"
            style={{ flexWrap: "nowrap" }}
          >
            {/* Tooltip для обраного значення */}
            <Tooltip
              openDelay={500}
              label={selectedItem?.label}
              position="top"
              offset={6}
              disabled={isTouchDevice}
              styles={{
                tooltip: {
                  ...commonStyles,
                  border: "1px solid gray",
                },
                arrow: {
                  backgroundColor: theme.colors.gray[1],
                  border: "1px solid gray",
                },
              }}
            >
              <Text size="sm" truncate="end">
                {selectedItem?.label || "Select..."}
              </Text>
            </Tooltip>

            {/* AI іконка */}
            {selectedItem?.autogenerated && (
              <AITooltip
                icon={
                  <Box
                    ml={8}
                    w={24}
                    h={24}
                    style={{
                      borderRadius: "50%",
                      backgroundColor: theme.colors.violet[1],
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <BotMessageSquareIcon
                      size={14}
                      color={theme.colors.violet[7]}
                    />
                  </Box>
                }
                title={t("generated_by_ai")}
              />
            )}
          </Group>
        </InputBase>
      </ComboboxTarget>

      <Combobox.Dropdown w={mobile ? 240 : 300}>
        <Combobox.Options>
          {data.map((item) => (
            <Combobox.Option key={item.value} value={item.value}>
              <Group justify="space-between" style={{ flexWrap: "nowrap" }}>
                {/* Tooltip для елементів списку */}
                <Tooltip
                  openDelay={500}
                  label={item.label}
                  position="top"
                  offset={6}
                  disabled={isTouchDevice}
                  styles={{
                    tooltip: {
                      ...commonStyles,
                      border: "1px solid gray",
                    },
                    arrow: {
                      backgroundColor: theme.colors.gray[1],
                      border: "1px solid gray",
                    },
                  }}
                >
                  <Text size="sm" truncate="end">
                    {item.label.length > 32
                      ? item.label.slice(0, 32) + "..."
                      : item.label}
                  </Text>
                </Tooltip>

                {item.autogenerated && (
                  <AITooltip
                    icon={
                      <Box
                        ml={8}
                        w={24}
                        h={24}
                        style={{
                          borderRadius: "50%",
                          backgroundColor: theme.colors.violet[1],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <BotMessageSquareIcon
                          size={14}
                          color={theme.colors.violet[7]}
                        />
                      </Box>
                    }
                    title={t("generated_by_ai")}
                    zIndex={301}
                  />
                )}
              </Group>
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
